import { Controller } from "@hotwired/stimulus"

function nowMs() {
  return typeof performance !== "undefined" ? performance.now() : Date.now()
}

export default class extends Controller {
  static targets = ["status", "detail", "startButton", "stopButton", "match", "transcript", "feedback"]
  static values = {
    workerUrl: String,
    chapterId: Number,
    modelUrl: String,
    vocabUrl: String,
    quranDataUrl: String
  }

  connect() {
    this.ready = false
    this.active = false
    this.booting = false
    this.starting = false
    this.debugPrefix = "[follow-along]"
    this.timingPrefix = "[follow-along:timing]"
    this.mediaStream = null
    this.audioContext = null
    this.sourceNode = null
    this.processorNode = null
    this.muteNode = null
    this.audioChunkCount = 0
    this.lastAudioLogAt = 0
    this.currentSessionId = null
    this.currentVerseKey = null
    this.renderedVerseKey = null
    this.highlightedVerseElements = []
    this.highlightedWordElements = []
    this.previewWordElements = []
    this.lastChunkTiming = null
    this.lastTranscriptTiming = null
    this.logDebug("controller connected")
    this.initializeWorker()
  }

  disconnect() {
    this.logDebug("controller disconnect")
    this.stopMicrophone()
    this.clearHighlights()
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
    }
  }

  async start() {
    if (!this.worker || !this.ready || this.active || this.starting) {
      this.logDebug("start ignored", {
        hasWorker: Boolean(this.worker),
        ready: this.ready,
        active: this.active,
        starting: this.starting
      })
      return
    }

    this.starting = true
    this.updateStatus("Microphone")
    this.updateDetail("Requesting microphone access…")
    this.logDebug("starting microphone flow")
    this.syncButtons()

    try {
      await this.startMicrophone()
      this.logDebug("microphone ready, sending start to worker")
      this.worker.postMessage({ type: "start" })
    } catch (error) {
      this.starting = false
      this.active = false
      await this.stopMicrophone()
      this.logDebug("microphone start failed", { error: this.formatError(error) })
      this.updateStatus("Ready")
      this.updateDetail(error instanceof Error ? error.message : "Unable to access microphone.")
      this.syncButtons()
    }
  }

  async stop() {
    if (!this.worker || (!this.active && !this.starting)) {
      this.logDebug("stop ignored", {
        hasWorker: Boolean(this.worker),
        active: this.active,
        starting: this.starting
      })
      return
    }

    this.starting = false
    this.active = false
    this.logDebug("stopping follow-along")
    await this.stopMicrophone()
    this.worker.postMessage({ type: "stop" })
    this.updateStatus("Ready")
    this.updateDetail("Follow-along stopped.")
    this.resetTrackingDisplay()
    this.syncButtons()
  }

  initializeWorker() {
    if (typeof Worker === "undefined") {
      this.updateStatus("Unsupported")
      this.updateDetail("This browser does not support Web Workers.")
      this.syncButtons()
      return
    }

    this.booting = true
    this.updateStatus("Loading")
    this.updateDetail("Preparing follow-along worker…")
    this.syncButtons()

    const workerUrl = this.hasWorkerUrlValue ? this.workerUrlValue : "/assets/workers/follow_along_worker.js"
    this.logDebug("initializing worker", {
      workerUrl,
      chapterId: this.chapterIdValue,
      modelUrl: this.modelUrlValue,
      vocabUrl: this.vocabUrlValue,
      quranDataUrl: this.quranDataUrlValue
    })
    this.worker = new Worker(workerUrl, { type: "module" })
    this.worker.onmessage = this.handleWorkerMessage.bind(this)
    this.worker.onerror = this.handleWorkerError.bind(this)
    this.worker.onmessageerror = this.handleWorkerMessageError.bind(this)
    this.worker.postMessage({
      type: "init",
      payload: {
        chapterId: this.chapterIdValue,
        modelUrl: this.modelUrlValue,
        vocabUrl: this.vocabUrlValue,
        quranDataUrl: this.quranDataUrlValue
      }
    })
  }

  handleWorkerMessage(event) {
    const message = event.data || {}
    const chunk = Number(message.chunk)
    const messageSessionId = Number(message.session_id)
    if (Number.isFinite(chunk) && (!this.lastChunkTiming || this.lastChunkTiming.chunk !== chunk)) {
      this.lastChunkTiming = {
        chunk,
        receivedAt: nowMs()
      }
    }

    if (message.type && !["loading", "loading_status"].includes(message.type)) {
      this.logDebug(`worker -> ${message.type}`, message)
    }

    if (message.type === "started" && Number.isFinite(messageSessionId)) {
      this.currentSessionId = messageSessionId
    } else if (
      Number.isFinite(messageSessionId) &&
      this.currentSessionId !== null &&
      messageSessionId !== this.currentSessionId
    ) {
      this.logDebug("ignoring stale worker session message", {
        type: message.type,
        chunk: message.chunk,
        sessionId: messageSessionId,
        currentSessionId: this.currentSessionId
      })
      return
    }

    const resultTypes = ["raw_transcript", "verse_match", "word_progress", "word_correction", "diagnostic"]
    if (!this.active && !this.starting && resultTypes.includes(message.type)) {
      this.logDebug("ignoring late worker result", { type: message.type, chunk: message.chunk })
      return
    }

    switch (message.type) {
      case "loading":
        this.updateStatus("Loading")
        this.updateDetail(`Downloading model… ${message.percent ?? 0}%`)
        break
      case "loading_status":
        this.updateStatus("Loading")
        this.updateDetail(message.message || "Preparing follow-along worker…")
        break
      case "ready":
        this.booting = false
        this.starting = false
        this.ready = true
        this.active = false
        this.updateStatus("Ready")
        this.updateDetail(message.message || "Worker initialized.")
        this.updateMatch("Waiting for a verse match…")
        this.updateTranscript("No transcript yet.")
        this.updateFeedback("No correction feedback yet.")
        break
      case "started":
        this.starting = false
        this.active = true
        this.updateStatus("Listening")
        this.updateDetail(message.message || "Follow-along started.")
        break
      case "stopped":
        this.starting = false
        this.active = false
        this.currentSessionId = null
        this.updateStatus("Ready")
        this.updateDetail(message.message || "Follow-along stopped.")
        break
      case "raw_transcript":
        this.handleRawTranscript(message)
        break
      case "verse_match":
        this.handleVerseMatch(message)
        break
      case "word_progress":
        this.handleWordProgress(message)
        break
      case "word_correction":
        this.handleWordCorrection(message)
        break
      case "diagnostic":
        this.handleDiagnostic(message.event)
        break
      case "error":
        console.error("Follow-along worker reported error", message)
        this.booting = false
        this.starting = false
        this.ready = false
        this.active = false
        this.stopMicrophone()
        this.updateStatus("Error")
        this.updateDetail(message.message || "Follow-along failed to initialize.")
        break
      default:
        break
    }

    this.syncButtons()
  }

  handleWorkerError(event) {
    console.error("Follow-along worker crashed", event)
    this.booting = false
    this.starting = false
    this.ready = false
    this.active = false
    this.stopMicrophone()
    this.updateStatus("Error")
    const location = [event.filename, event.lineno, event.colno].filter(Boolean).join(":")
    const detail = [event.message, location].filter(Boolean).join(" @ ")
    this.updateDetail(detail || "Worker crashed.")
    this.syncButtons()
  }

  handleWorkerMessageError(event) {
    console.error("Follow-along worker message deserialization failed", event)
  }

  async startMicrophone() {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("This browser does not support microphone capture.")
    }

    if (this.mediaStream) {
      return
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) {
      throw new Error("This browser does not support Web Audio capture.")
    }

    this.mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false
      }
    })

    this.audioContext = new AudioContextClass()
    await this.audioContext.resume()
    this.audioChunkCount = 0
    this.lastAudioLogAt = 0

    const [track] = this.mediaStream.getAudioTracks()
    this.logDebug("microphone stream ready", {
      trackLabel: track?.label,
      trackState: track?.readyState,
      trackSettings: track?.getSettings ? track.getSettings() : null,
      sampleRate: this.audioContext.sampleRate,
      audioState: this.audioContext.state
    })

    this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
    this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1)
    this.muteNode = this.audioContext.createGain()
    this.muteNode.gain.value = 0

    this.processorNode.onaudioprocess = (event) => {
      if (!this.worker || !this.active) {
        return
      }

      const input = event.inputBuffer.getChannelData(0)
      const samples = this.downsampleBuffer(input, this.audioContext.sampleRate, 16000)
      if (samples.length === 0) {
        return
      }

      this.audioChunkCount += 1
      this.logAudioChunk(samples, input.length)

      this.worker.postMessage({ type: "audio", samples }, [samples.buffer])
    }

    this.sourceNode.connect(this.processorNode)
    this.processorNode.connect(this.muteNode)
    this.muteNode.connect(this.audioContext.destination)
  }

  async stopMicrophone() {
    this.logDebug("stop microphone", {
      hadProcessor: Boolean(this.processorNode),
      hadSource: Boolean(this.sourceNode),
      hadStream: Boolean(this.mediaStream),
      hadAudioContext: Boolean(this.audioContext)
    })

    if (this.processorNode) {
      this.processorNode.onaudioprocess = null
      this.processorNode.disconnect()
      this.processorNode = null
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect()
      this.sourceNode = null
    }

    if (this.muteNode) {
      this.muteNode.disconnect()
      this.muteNode = null
    }

    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) {
        track.stop()
      }
      this.mediaStream = null
    }

    if (this.audioContext) {
      const context = this.audioContext
      this.audioContext = null
      await context.close()
    }
  }

  downsampleBuffer(input, inputRate, outputRate) {
    if (!input?.length) {
      return new Float32Array(0)
    }

    if (inputRate <= outputRate) {
      return new Float32Array(input)
    }

    const ratio = inputRate / outputRate
    const outputLength = Math.max(1, Math.round(input.length / ratio))
    const output = new Float32Array(outputLength)

    let outputIndex = 0
    let inputIndex = 0

    while (outputIndex < outputLength) {
      const nextInputIndex = Math.min(input.length, Math.round((outputIndex + 1) * ratio))
      let sum = 0
      let count = 0

      for (let i = inputIndex; i < nextInputIndex; i++) {
        sum += input[i]
        count += 1
      }

      output[outputIndex] = count > 0 ? sum / count : input[Math.min(inputIndex, input.length - 1)]
      outputIndex += 1
      inputIndex = nextInputIndex
    }

    return output
  }

  handleRawTranscript(message) {
    const chunk = Number(message.chunk)
    this.lastTranscriptTiming = {
      receivedAt: Number.isFinite(chunk) && this.lastChunkTiming?.chunk === chunk
        ? this.lastChunkTiming.receivedAt
        : nowMs(),
      chunk: Number.isFinite(chunk) ? chunk : null,
      text: message.text || "",
      tokenCount: message.token_count,
      finalFlush: message.final_flush
    }
    this.logDebug("raw transcript", {
      text: message.text,
      confidence: message.confidence,
      rawPhonemes: message.raw_phonemes,
      tokenCount: message.token_count,
      short: message.short,
      finalFlush: message.final_flush
    })
  }

  handleVerseMatch(message) {
    this.logDebug("verse match", {
      surah: message.surah,
      ayah: message.ayah,
      confidence: message.confidence,
      verseText: message.verse_text
    })
    const verseKey = `${message.surah}:${message.ayah}`
    const label = message.surah_name ? `${message.surah_name} • ${verseKey}` : verseKey
    const confidence = typeof message.confidence === "number" ? ` (${message.confidence.toFixed(2)})` : ""

    this.currentVerseKey = verseKey
    const highlightStartedAt = nowMs()
    this.updateMatch(`${label}${confidence}`)
    this.updateMatchedTranscript()
    this.updateDetail(`Matched verse ${verseKey}.`)
    this.highlightVerse(verseKey, { scrollIntoView: true, force: true })
    this.clearWordProgress()
    this.logHighlightLatency("verse_match", verseKey, highlightStartedAt, {}, message.chunk)
  }

  handleWordProgress(message) {
    this.logDebug("word progress", {
      surah: message.surah,
      ayah: message.ayah,
      wordIndex: message.word_index,
      previewWordIndex: message.preview_word_index,
      totalWords: message.total_words,
      matchedIndices: message.matched_indices,
      previewIndices: message.preview_indices
    })
    const verseKey = `${message.surah}:${message.ayah}`
    const verseChanged = this.currentVerseKey !== verseKey
    this.currentVerseKey = verseKey
    const highlightStartedAt = nowMs()
    this.highlightVerse(verseKey, { scrollIntoView: verseChanged, force: verseChanged })
    this.highlightWords(verseKey, message.matched_indices || [], message.preview_indices || [])

    const confirmedWord = Math.max(Number(message.word_index || 0), 0)
    const previewWord = Number(message.preview_word_index || 0)
    const previewSuffix = previewWord > confirmedWord ? ` · preview ${previewWord}` : ""
    this.updateMatchedTranscript()
    this.updateDetail(`Tracking ${verseKey} · word ${confirmedWord} of ${message.total_words}${previewSuffix}`)
    this.logHighlightLatency("word_progress", verseKey, highlightStartedAt, {
      confirmed_word: confirmedWord,
      preview_word: previewWord,
      total_words: message.total_words
    }, message.chunk)
  }

  handleWordCorrection(message) {
    this.logDebug("word correction", {
      surah: message.surah,
      ayah: message.ayah,
      corrections: message.corrections
    })
    const verseKey = `${message.surah}:${message.ayah}`
    const summary = (message.corrections || [])
      .slice(0, 3)
      .map((correction) => {
        const expected = correction.expected || "…"
        const got = correction.got || "…"
        const wordNumber = (correction.display_word_index ?? correction.word_index) + 1
        return `w${wordNumber}: ${got} → ${expected}`
      })
      .join(" · ")

    this.updateFeedback(summary ? `${verseKey} ${summary}` : "No correction feedback yet.")
  }

  handleDiagnostic(event) {
    if (!event || typeof event !== "object") {
      return
    }

    this.logDebug("diagnostic", event)

    if (event.type === "silence_skip" && event.reason === "transcript too short") {
      this.updateDetail(`Transcript too short: ${event.text || "∅"}`)
    }

    if (event.type === "commit") {
      this.updateDetail(`Committed ${event.ref} (${event.reason}).`)
    } else if (event.type === "rollback") {
      this.updateDetail(`Tracking rollback: ${event.reason}.`)
    } else if (event.type === "stale_exit") {
      this.updateDetail(`Tracking reset after stale audio on ${event.ref}.`)
    }
  }

  updateMatch(message) {
    if (this.hasMatchTarget) {
      this.matchTarget.textContent = message
    }
  }

  updateTranscript(message) {
    if (this.hasTranscriptTarget) {
      this.transcriptTarget.textContent = message
    }
  }

  updateFeedback(message) {
    if (this.hasFeedbackTarget) {
      this.feedbackTarget.textContent = message
    }
  }

  updateMatchedTranscript() {
    const text = this.lastTranscriptTiming?.text?.trim()
    if (!text) {
      return
    }

    this.updateTranscript(text)
  }

  logHighlightLatency(eventType, verseKey, highlightStartedAt, extra = {}, chunk = null) {
    const resolvedChunk = Number.isFinite(Number(chunk)) ? Number(chunk) : null
    const chunkTiming = resolvedChunk !== null && this.lastChunkTiming?.chunk === resolvedChunk
      ? this.lastChunkTiming
      : null
    const transcriptTiming = resolvedChunk !== null && this.lastTranscriptTiming?.chunk === resolvedChunk
      ? this.lastTranscriptTiming
      : this.lastTranscriptTiming

    const receivedAt = chunkTiming?.receivedAt ?? transcriptTiming?.receivedAt
    if (!receivedAt) {
      return
    }

    const finishedAt = nowMs()
    console.error(this.timingPrefix, "transcript to highlight", {
      event: eventType,
      verse_key: verseKey,
      chunk: resolvedChunk,
      elapsed_ms: Number((finishedAt - receivedAt).toFixed(1)),
      highlight_ms: Number((finishedAt - highlightStartedAt).toFixed(1)),
      transcript_text: transcriptTiming?.text,
      token_count: transcriptTiming?.tokenCount,
      final_flush: transcriptTiming?.finalFlush,
      ...extra
    })
  }

  highlightVerse(verseKey, options = {}) {
    const { scrollIntoView = true, force = false } = options
    if (!force && this.renderedVerseKey === verseKey) {
      return
    }

    this.clearVerseHighlight()

    const elements = this.findVerseWordElements(verseKey)
    this.highlightedVerseElements = elements
    this.renderedVerseKey = verseKey
    for (const element of elements) {
      element.style.backgroundColor = "rgba(59, 130, 246, 0.12)"
      element.style.borderRadius = "0.35rem"
      element.style.boxShadow = "inset 0 -2px 0 rgba(37, 99, 235, 0.35)"
    }

    const firstElement = elements[0]
    if (scrollIntoView && firstElement) {
      firstElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" })
    }
  }

  highlightWords(verseKey, matchedIndices, previewIndices = []) {
    this.clearWordProgress()

    const positions = new Set((matchedIndices || []).map((index) => String(index + 1)))
    const previewPositions = new Set((previewIndices || []).map((index) => String(index + 1)))
    const verseElements = this.findVerseWordElements(verseKey)
    const elements = verseElements.filter((element) => positions.has(element.dataset.position))
    this.highlightedWordElements = elements
    for (const element of elements) {
      element.style.backgroundColor = "rgba(16, 185, 129, 0.18)"
      element.style.borderRadius = "0.35rem"
      element.style.boxShadow = "inset 0 -2px 0 rgba(5, 150, 105, 0.45)"
    }

    const previewElements = verseElements.filter((element) => previewPositions.has(element.dataset.position) && !positions.has(element.dataset.position))
    this.previewWordElements = previewElements
    for (const element of previewElements) {
      element.style.backgroundColor = "rgba(250, 204, 21, 0.18)"
      element.style.borderRadius = "0.35rem"
      element.style.boxShadow = "inset 0 -2px 0 rgba(202, 138, 4, 0.45)"
    }
  }

  clearHighlights() {
    this.clearWordProgress()
    this.clearVerseHighlight()
  }

  clearVerseHighlight() {
    for (const element of this.highlightedVerseElements) {
      element.style.backgroundColor = ""
      element.style.borderRadius = ""
      element.style.boxShadow = ""
    }
    this.highlightedVerseElements = []
    this.renderedVerseKey = null
  }

  clearWordProgress() {
    for (const element of this.highlightedWordElements) {
      element.style.backgroundColor = ""
      element.style.borderRadius = ""
      element.style.boxShadow = ""
    }
    this.highlightedWordElements = []

    for (const element of this.previewWordElements) {
      element.style.backgroundColor = ""
      element.style.borderRadius = ""
      element.style.boxShadow = ""
    }
    this.previewWordElements = []
  }

  resetTrackingDisplay() {
    this.currentVerseKey = null
    this.clearHighlights()
    this.updateFeedback("No correction feedback yet.")
  }

  findVerseWordElements(verseKey) {
    const escapedVerseKey = typeof CSS !== "undefined" && CSS.escape ? CSS.escape(verseKey) : verseKey
    return Array.from(document.querySelectorAll(`[data-ayah="${escapedVerseKey}"][data-position]`))
  }

  updateStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
    }
  }

  updateDetail(message) {
    if (this.hasDetailTarget) {
      this.detailTarget.textContent = message
    }
  }

  syncButtons() {
    if (this.hasStartButtonTarget) {
      this.startButtonTarget.disabled = !this.ready || this.active || this.booting || this.starting
    }

    if (this.hasStopButtonTarget) {
      this.stopButtonTarget.disabled = !this.active && !this.starting
    }
  }

  logAudioChunk(samples, inputLength) {
    const now = Date.now()
    if (now - this.lastAudioLogAt < 1000) {
      return
    }

    this.lastAudioLogAt = now
    this.logDebug("audio chunk", {
      chunk: this.audioChunkCount,
      inputLength,
      outputLength: samples.length,
      stats: this.computeAudioStats(samples)
    })
  }

  computeAudioStats(samples) {
    let min = Infinity
    let max = -Infinity
    let sumSq = 0

    for (let i = 0; i < samples.length; i++) {
      const value = samples[i]
      if (value < min) min = value
      if (value > max) max = value
      sumSq += value * value
    }

    const rms = samples.length > 0 ? Math.sqrt(sumSq / samples.length) : 0
    return {
      min: Number.isFinite(min) ? Number(min.toFixed(4)) : 0,
      max: Number.isFinite(max) ? Number(max.toFixed(4)) : 0,
      rms: Number(rms.toFixed(4))
    }
  }

  formatError(error) {
    if (error instanceof Error) {
      return `${error.name}: ${error.message}`
    }

    return String(error)
  }

  logDebug(message, payload = null) {
    if (payload === null) {
      console.log(this.debugPrefix, message)
      return
    }

    console.log(this.debugPrefix, message, payload)
  }
}
